import { Cards, FilmStrip, Graph, ListNumbers } from '@phosphor-icons/react';
import type { IdeaDto, PublicationKind } from '@app/shared';
import { useT } from '../i18n';
import { adviceKind, shownKind } from '../lib/idea-kinds';
import { HelpHint } from './HelpHint';
import { Badge, Button, Card } from './ui';

export interface IdeaCardProps {
  idea: IdeaDto;
  /** The kinds the passport offers now. */
  kinds: PublicationKind[];
  /** «Взять» with the advised format, or «Открыть» a taken idea. */
  onTake: (idea: IdeaDto, kind: PublicationKind) => void;
  taking: boolean;
  disabled?: boolean;
  /** Set by the page when «Взять» was refused for this format: the advice falls back to «Сцены». */
  fallback?: boolean;
}

const KIND_ICON: Record<PublicationKind, JSX.Element> = {
  reel_scenes: <FilmStrip size={14} />,
  reel_diagram: <Graph size={14} />,
  reel_listicle: <ListNumbers size={14} />,
  carousel: <Cards size={14} />,
};

/**
 * One idea (spec 3): the angle, the hook, what it is about and the advice — the
 * format the brain chose and why. The format itself is chosen after «Взять», on
 * «Формат и текст», with an example in front of the person.
 */
export function IdeaCard({ idea, kinds, onTake, taking, disabled = false, fallback = false }: IdeaCardProps) {
  const t = useT();
  const taken = idea.draftId !== null;
  const advice = fallback ? 'reel_scenes' : adviceKind(idea, kinds);
  const shown = shownKind(idea, advice);
  return (
    <Card variant="surface" padding="none" className="p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge tone="neutral" className="break-words">
          {idea.angle}
        </Badge>
        {taken && (
          <Badge tone="ink" leftIcon={KIND_ICON[shown]}>
            {t.kinds[shown]}
          </Badge>
        )}
      </div>
      <p className="text-lg font-bold leading-snug mb-1 break-words">{idea.hook}</p>
      <p className="text-sm mb-3 break-words">{idea.summary}</p>
      {!taken && (
        <div className="rounded-2xl bg-accent-soft text-accent-soft-on px-3 py-2.5 mb-3">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            {KIND_ICON[advice]}
            <span className="break-words">{t.ideas.advice(t.ideas.adviceKinds[advice])}</span>
            <HelpHint topic="advice" className="bg-surface/70" />
          </p>
          {/* The reason is the brain's: it fits only the format the brain chose. */}
          {advice === idea.kind && <p className="text-xs mt-1 opacity-80 break-words">{idea.whyFormat}</p>}
        </div>
      )}
      <Button
        variant={taken ? 'secondary' : 'ink'}
        size="md"
        fullWidth
        loading={taking}
        disabled={disabled}
        onClick={() => onTake(idea, advice)}
      >
        {taken ? t.ideas.open : t.ideas.take}
      </Button>
    </Card>
  );
}
